import { Component, OnInit, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { UserService } from '../../Services/user.service';
import { SweetAlertService } from 'src/app/Services/sweet-alert.service';
import { WoocommerceService } from 'src/app/Services/woocommerce.service';
import { Country, State } from 'src/app/models/woocommerce.model';
import { startWith, switchMap } from 'rxjs/operators';
import { AuthService } from 'src/app/Services/auth.service';
import { DeviceDetectionService } from 'src/app/Services/device-detection.service';
import { PhoneVerificationService } from 'src/app/Services/phone-verification.service';

@Component({
    selector: 'app-billing-profile-modal',
    templateUrl: './billing-profile-modal.component.html',
    styleUrls: ['./billing-profile-modal.component.css'],
    standalone: false
})
export class BillingProfileModalComponent implements OnInit {

    form: FormGroup;
    countries: Country[] = [];
    filteredCountries: Country[] = [];
    states: State[] = [];
    filteredStates: State[] = [];
    countryControl = new FormControl('');
    stateControl = new FormControl('');

    constructor(
        private fb: FormBuilder,
        public dialogRef: MatDialogRef<BillingProfileModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private userService: UserService,
        private sweetAlertService: SweetAlertService,
        private woocommerceService: WoocommerceService,
        private authService: AuthService,
        private dialog: MatDialog,
        private deviceDetectionService: DeviceDetectionService,
        private phoneVerificationService: PhoneVerificationService
    ) {
        this.form = this.fb.group({
            country: ['', [Validators.required]],
            state: ['', []],
            // phoneNumber: ['', [Validators.required, Validators.pattern('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\\s\\./0-9]*$')]],
            address1: ['', [Validators.required]],
            address2: [''],
            city: ['', [Validators.required]],
            zipCode: ['', [Validators.required]]
        });
    }

    ngOnInit(): void {
        this.woocommerceService.getCountries().subscribe((countries: Country[]) => {
            this.countries = countries;
            this.filteredCountries = countries;

            const user = this.data;
            if (user) {
                this.form.patchValue({
                    address1: user.meta?.billing_address_1 || user.billing_address?.address_1,
                    address2: user.meta?.billing_address_2 || user.billing_address?.address_2,
                    city: user.meta?.billing_city || user.billing_address?.city,
                    zipCode: user.meta?.billing_postcode || user.billing_address?.postcode
                });

                const countryCode = user.meta?.billing_country || user.billing_address?.country;
                const savedState = user.meta?.billing_state || user.billing_address?.state;

                if (countryCode) {
                    this.form.get('country')?.setValue(countryCode);
                    this.countryControl.setValue(countryCode, { emitEvent: false });
                    this.loadStatesAfterCountrySelect(countryCode, savedState || null);
                }
            }

            this._filterCountries(this.countryControl.value || '');
            this._filterStates(this.stateControl.value || '');
        });

        this.countryControl.valueChanges
            .pipe(startWith(''))
            .subscribe(value => this._filterCountries(value || ''));

        this.stateControl.valueChanges
            .pipe(startWith(''))
            .subscribe(value => this._filterStates(value || ''));
    }

    displayCountry(countryCode: string): string {
        const country = this.countries.find(c => c.code === countryCode);
        return country ? country.name : '';
    }

    displayState(stateCode: string): string {
        const state = this.states.find(s => s.code === stateCode);
        return state ? state.name : '';
    }

    private _filterCountries(value: string): void {
        const filterValue = value.toLowerCase();
        this.filteredCountries = this.countries.filter(country =>
            country.name.toLowerCase().includes(filterValue) ||
            country.code.toLowerCase().includes(filterValue)
        );
    }

    private _filterStates(value: string): void {
        const filterValue = value.toLowerCase();
        this.filteredStates = this.states.filter(state =>
            state.name.toLowerCase().includes(filterValue) ||
            state.code.toLowerCase().includes(filterValue)
        );
    }

    /**
     * Loads states for a country (embedded list or WooCommerce /countries/{code}/states)
     * and applies validators. Optionally restores a saved state code/name after load.
     */
    private loadStatesAfterCountrySelect(countryCode: string, restoreState: string | null): void {
        const selectedCountry = this.countries.find(c => c.code === countryCode);

        const applyStateList = (stateList: State[]) => {
            const list = stateList ?? [];
            if (list.length > 0) {
                this.states = list;
                this.filteredStates = list;
                this.form.get('state')?.setValidators([Validators.required]);
            } else {
                this.states = [];
                this.filteredStates = [];
                this.form.get('state')?.clearValidators();
            }
            this.form.get('state')?.updateValueAndValidity();

            if (restoreState) {
                const rs = restoreState.trim();
                const match = list.find(
                    s => s.code.toLowerCase() === rs.toLowerCase() || s.name.toLowerCase() === rs.toLowerCase()
                );
                if (match) {
                    this.form.get('state')?.setValue(match.code);
                    this.stateControl.setValue(match.code, { emitEvent: false });
                } else {
                    this.form.get('state')?.setValue(rs);
                    this.stateControl.setValue(rs, { emitEvent: false });
                }
            } else {
                this.stateControl.setValue('');
                this.form.get('state')?.setValue('');
            }
        };

        if (!selectedCountry) {
            applyStateList([]);
            return;
        }

        if (selectedCountry.states?.length) {
            applyStateList(selectedCountry.states);
            return;
        }

        this.states = [];
        this.filteredStates = [];
        this.form.get('state')?.setValidators([Validators.required]);
        this.form.get('state')?.setValue('');
        this.stateControl.setValue('', { emitEvent: false });
        this.form.get('state')?.updateValueAndValidity();

        this.woocommerceService.getStatesForCountry(selectedCountry.code).subscribe({
            next: (fetched: State[]) => {
                const list = fetched ?? [];
                selectedCountry.states = list;
                applyStateList(list);
            },
            error: () => applyStateList([])
        });
    }

    onCountrySelected(countryCode: string) {
        this.form.get('country')?.setValue(countryCode);
        this.countryControl.setValue(countryCode, { emitEvent: false });
        this.loadStatesAfterCountrySelect(countryCode, null);
    }

    onStateSelected(stateCode: string) {
        this.form.get('state')?.setValue(stateCode);
    }

    onCountryInputBlur(): void {
        const raw = (this.countryControl.value ?? '').toString().trim();
        if (!raw) {
            this.form.get('country')?.setValue('');
            this.form.get('country')?.markAsTouched();
            this.form.get('country')?.updateValueAndValidity();
            return;
        }
        const byCode = this.countries.find(c => c.code.toLowerCase() === raw.toLowerCase());
        const byName = this.countries.find(c => c.name.toLowerCase() === raw.toLowerCase());
        const matched = byCode || byName;
        if (matched) {
            this.countryControl.setValue(matched.code, { emitEvent: false });
            this.form.get('country')?.setValue(matched.code);
            this.loadStatesAfterCountrySelect(matched.code, null);
        } else {
            this.form.get('country')?.setValue('');
        }
        this.form.get('country')?.markAsTouched();
        this.form.get('country')?.updateValueAndValidity();
    }

    onStateInputBlur(): void {
        const raw = (this.stateControl.value ?? '').toString().trim();
        const stateFc = this.form.get('state');
        if (!this.states.length) {
            stateFc?.setValue(raw);
            stateFc?.markAsTouched();
            stateFc?.updateValueAndValidity();
            return;
        }
        if (!raw) {
            stateFc?.setValue('');
        } else {
            const byCode = this.states.find(s => s.code.toLowerCase() === raw.toLowerCase());
            const byName = this.states.find(s => s.name.toLowerCase() === raw.toLowerCase());
            const matched = byCode || byName;
            if (matched) {
                this.stateControl.setValue(matched.code, { emitEvent: false });
                stateFc?.setValue(matched.code);
            } else {
                stateFc?.setValue('');
            }
        }
        stateFc?.markAsTouched();
        stateFc?.updateValueAndValidity();
    }

    onSave(): void {
        if (this.form.valid) {
            const user = this.userService.getCurrentUserValue();
            if (!user) {
                this.sweetAlertService.showAlert('Error!', 'User not found. Please log in again.', 'error');
                return;
            }

            const performSave = () => {
                const billingAddress = {
                    first_name: user.name.split(' ')[0] || '',
                    last_name: user.name.split(' ')[1] || '',
                    company: user.meta.company_name || '',
                    address_1: this.form.value.address1,
                    address_2: this.form.value.address2,
                    city: this.form.value.city,
                    state: this.form.value.state,
                    postcode: this.form.value.zipCode,
                    country: this.form.value.country,
                    email: user.email,
                    // phone: this.form.value.phoneNumber,
                };

                const updatedData = {
                    billing: billingAddress
                };

                this.woocommerceService.updateCustomerBilling(user.id, updatedData).pipe(
                    switchMap(() => this.userService.fetchCurrentUser())
                ).subscribe((updatedUser) => {
                    this.authService.fetchUserDataAndSetup(updatedUser);
                    setTimeout(() => {
                        this.sweetAlertService.showAlert('Success!', 'Billing address saved successfully.', 'success');
                    }, 500);
                    this.dialogRef.close(true);
                }, () => {
                    this.sweetAlertService.showAlert('Error!', 'Failed to save billing address.', 'error');
                });
            };

            // Phone verification is now done before opening billing modal
            // Just proceed with saving
            performSave();
        }
    }

    onCancel(): void {
        this.dialogRef.close(false);
    }
} 